import { useEffect, useMemo, useRef, useState } from "react";

import {
  CountsError,
  getInRoom,
  getStageInRoom,
  type InRoomAttendee,
  type InRoomResponse
} from "../api/client.js";

/**
 * Attendees pill — sits next to the Snapshot button (CR-03). Click opens a
 * full-screen modal that lists every attendee whose latest scan was an
 * ENTER, sorted alphabetically by name, filterable by a single smart-search
 * box that matches across every field the row carries.
 *
 * The modal re-fetches on each open. Inside the dialog there's no live
 * polling — operators close it when they're done. The board behind the
 * dialog keeps its existing 3s poll.
 */
export function AttendeesButton({
  eventId,
  stageId,
  token
}: {
  eventId: string;
  stageId?: string;
  token: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="attendees-button"
        className="monitor-no-print inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-sm text-slate-200 transition-colors hover:bg-slate-800"
      >
        <span className="h-2 w-2 rounded-full bg-slate-500" aria-hidden />
        Attendees
      </button>
      {open ? (
        <AttendeesDialog
          eventId={eventId}
          stageId={stageId}
          token={token}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; data: InRoomResponse }
  | { kind: "error"; message: string };

export function AttendeesDialog({
  eventId,
  stageId,
  token,
  onClose
}: {
  eventId: string;
  stageId?: string;
  token: string;
  onClose: () => void;
}): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // Initial fetch + close-on-escape.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data =
          stageId !== undefined
            ? await getStageInRoom(stageId, token)
            : await getInRoom(eventId, token);
        if (cancelled) return;
        setState({ kind: "loaded", data });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof CountsError
            ? err.status === 401
              ? "Invalid link"
              : err.status === 404
                ? stageId !== undefined
                  ? "Stage not found"
                  : "Event not found"
                : err.code === "NETWORK"
                  ? "Network error"
                  : "Could not load attendees"
            : "Could not load attendees";
        setState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, stageId, token]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Focus the search box so the operator can type immediately.
    searchRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const all = state.kind === "loaded" ? state.data.in_room : [];
  const filtered = useMemo(() => filterAttendees(all, query), [all, query]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Attendees in the room"
      data-testid="attendees-dialog"
      className="monitor-no-print fixed inset-0 z-50 flex items-stretch justify-center bg-black/80 p-0 sm:items-center sm:p-6"
      onClick={(e) => {
        // Click on the backdrop closes; clicks inside the card stay.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-full w-full flex-col overflow-hidden bg-slate-950 text-slate-100 sm:max-w-3xl sm:rounded-lg sm:border sm:border-slate-800">
        <header className="flex flex-col gap-3 border-b border-slate-800 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold sm:text-lg">
              In the room
              {state.kind === "loaded" ? (
                <span className="ml-2 text-slate-400">
                  · {filtered.length}
                  {filtered.length !== all.length ? (
                    <span className="text-slate-600"> / {all.length}</span>
                  ) : null}
                </span>
              ) : null}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="attendees-close"
              className="rounded-md px-2 py-1 text-slate-400 transition-colors hover:bg-slate-900 hover:text-slate-100"
            >
              ✕
            </button>
          </div>
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, company, email, source, group…"
            data-testid="attendees-search"
            className="w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-slate-600 focus:outline-none"
          />
        </header>

        <div className="flex-1 overflow-y-auto">
          {state.kind === "loading" ? (
            <p
              data-testid="attendees-loading"
              className="px-6 py-10 text-center text-sm uppercase tracking-[0.3em] text-slate-500"
            >
              Loading…
            </p>
          ) : null}
          {state.kind === "error" ? (
            <p
              role="alert"
              data-testid="attendees-error"
              className="px-6 py-10 text-center text-sm text-rose-300"
            >
              {state.message}
            </p>
          ) : null}
          {state.kind === "loaded" ? (
            <AttendeeList
              rows={filtered}
              total={all.length}
              truncated={state.data.in_room_truncated}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function AttendeeList({
  rows,
  total,
  truncated
}: {
  rows: InRoomAttendee[];
  total: number;
  truncated: boolean;
}): JSX.Element {
  if (total === 0) {
    return (
      <p
        data-testid="attendees-empty"
        className="px-6 py-10 text-center text-sm text-slate-400"
      >
        No attendees currently in the room.
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p
        data-testid="attendees-no-match"
        className="px-6 py-10 text-center text-sm text-slate-400"
      >
        No matches.
      </p>
    );
  }
  return (
    <ul data-testid="attendees-list" className="divide-y divide-slate-900">
      {rows.map((a) => (
        <li
          key={a.id}
          data-testid={`attendees-row-${a.id}`}
          className="px-4 py-3 sm:px-6"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="text-sm font-semibold text-slate-100 sm:text-base">
              {a.name}
            </span>
            <span className="text-xs uppercase tracking-wide text-slate-500">
              {capitalize(a.source)}
            </span>
          </div>
          {a.company || a.email ? (
            <p className="mt-1 text-xs text-slate-400">
              {a.company ?? ""}
              {a.company && a.email ? " · " : ""}
              {a.email ?? ""}
            </p>
          ) : null}
          {a.group_names.length > 0 ? (
            <div
              data-testid={`attendees-groups-${a.id}`}
              className="mt-1.5 flex flex-wrap gap-1"
            >
              {a.group_names.map((g) => (
                <span
                  key={g}
                  className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-300"
                >
                  {g}
                </span>
              ))}
            </div>
          ) : null}
          <p className="mt-1 font-mono text-[10px] text-slate-600">
            {a.meetpoint_participant_id !== null
              ? `mp ${String(a.meetpoint_participant_id)} · `
              : ""}
            {formatClock(a.last_enter_at)}
          </p>
        </li>
      ))}
      {truncated ? (
        <li
          data-testid="attendees-truncated"
          className="px-4 py-3 text-center text-xs italic text-slate-500"
        >
          List truncated — refine your search to narrow further.
        </li>
      ) : null}
    </ul>
  );
}

/**
 * Multi-token smart filter. Lowercases + splits the query on whitespace;
 * every token must appear as a substring in the row's combined haystack
 * (name + company + email + source + group names + mp id). Empty query
 * passes everything through.
 */
function filterAttendees(rows: InRoomAttendee[], query: string): InRoomAttendee[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return rows;
  return rows.filter((row) => {
    const haystack = buildHaystack(row);
    return tokens.every((t) => haystack.includes(t));
  });
}

function buildHaystack(row: InRoomAttendee): string {
  return [
    row.name,
    row.company ?? "",
    row.email ?? "",
    row.source,
    ...row.group_names,
    row.meetpoint_participant_id !== null
      ? String(row.meetpoint_participant_id)
      : ""
  ]
    .join(" ")
    .toLowerCase();
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatClock(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm} UTC`;
}
